<template>
  <div class="system-admin">
    <p class="hint">
      数据库文件包含全部小说、分集、资产信息与任务记录。建议定期下载备份以防数据丢失。
    </p>

    <el-card shadow="never" class="block-card">
      <div slot="header">下载数据库备份</div>
      <p class="desc">
        下载当前服务器的数据库文件（<code>story.db</code>）。该文件包含所有配置与任务数据（不含上传的原始图片/视频文件）。
      </p>
      <el-button type="primary" :loading="downloadLoading" @click="handleDownloadBackup">
        下载数据库备份（.db）
      </el-button>
    </el-card>

    <el-card shadow="never" class="block-card">
      <div slot="header">
        备份历史
        <span v-if="dbLastUpdated" style="font-weight: normal; font-size: 13px; color: #909399; margin-left: 12px;">
          数据库最后更新于 {{ formatTimestamp(dbLastUpdated) }}
        </span>
      </div>
      <p class="desc">
        服务器每小时自动检测数据变更并创建备份。列表显示最近 {{ backupKeepCount }} 个备份，可下载或恢复任意版本。
      </p>
      <div style="margin-bottom: 12px;">
        <el-button size="small" @click="loadBackups" :loading="backupListLoading">刷新列表</el-button>
      </div>
      <el-table :data="backups" border style="width: 100%" v-loading="backupListLoading" empty-text="暂无备份">
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column label="资源时间" width="170">
          <template slot-scope="scope">{{ formatTimestamp(scope.row.db_mtime) }}</template>
        </el-table-column>
        <el-table-column prop="size" label="文件大小" width="110" align="right">
          <template slot-scope="scope">{{ formatSize(scope.row.size) }}</template>
        </el-table-column>
        <el-table-column prop="backup_type" label="类型" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.backup_type === 'auto' ? '' : 'warning'" size="small">
              {{ scope.row.backup_type === 'auto' ? '自动' : '手动' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" align="center">
          <template slot-scope="scope">
            <el-button type="text" @click="handleBackupDownload(scope.row.filename)">下载</el-button>
            <el-divider direction="vertical" />
            <el-button type="text" class="color-danger" @click="handleBackupRestore(scope.row)">恢复</el-button>
            <el-divider direction="vertical" />
            <el-button type="text" class="color-danger" @click="handleBackupDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card shadow="never" class="block-card">
      <div slot="header">上传数据库恢复</div>
      <p class="desc">
        请上传 <code>.db</code> 格式的数据库文件。成功后将替换当前服务器数据库并立即生效，无需重启进程。
      </p>
      <el-upload
        drag
        action="#"
        :auto-upload="false"
        :limit="1"
        accept=".db,.sqlite"
        :file-list="fileList"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将 .db 文件拖到此处，或<em>点击选择</em></div>
        <div slot="tip" class="el-upload__tip">仅支持 .db 或 .sqlite 文件</div>
      </el-upload>
      <el-button
        type="danger"
        plain
        style="margin-top: 16px"
        :loading="restoreLoading"
        :disabled="!selectedFile"
        @click="handleRestore"
      >
        上传并替换数据库
      </el-button>
    </el-card>

    <el-card shadow="never" class="block-card">
      <div slot="header">故事列表管理</div>
      <el-table :data="novels" border style="width: 100%">
        <el-table-column prop="display_name" label="故事名称" min-width="120" />
        <el-table-column prop="novel_key" label="Key" width="150" />
        <el-table-column prop="created_at" label="创建时间" width="180" />
        <el-table-column label="操作" width="100" align="center">
          <template slot-scope="scope">
            <el-button
              type="text"
              @click="handleEditNovel(scope.row)"
            >
              编辑
            </el-button>
            <el-divider direction="vertical" v-if="scope.row.novel_key !== 'default'" />
            <el-button
              v-if="scope.row.novel_key !== 'default'"
              type="text"
              :class="scope.row.novel_key === currentNovelKey ? '' : 'color-danger'"
              :disabled="scope.row.novel_key === currentNovelKey"
              :title="scope.row.novel_key === currentNovelKey ? '不能删除当前正在使用的故事' : ''"
              @click="handleDeleteNovel(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-card shadow="never" class="block-card">
      <div slot="header">清理无效资源</div>
      <p class="desc">
        删除未被资产、剧情图、分段视频、任务记录引用的图片/视频文件。清理任务在后台异步执行，执行中按钮不可点击。
      </p>
      <el-button
        type="warning"
        :loading="cleanupRunning"
        :disabled="cleanupRunning"
        @click="handleStartCleanup"
      >
        {{ cleanupRunning ? "正在清理中" : "清理" }}
      </el-button>
      <p v-if="cleanupLastResult" class="cleanup-result">
        最近一次清理：扫描 {{ cleanupLastResult.scanned_files || 0 }} 个文件，删除
        {{ cleanupLastResult.deleted_files || 0 }} 个文件
        <span v-if="cleanupLastResult.error" class="color-danger">（失败：{{ cleanupLastResult.error }}）</span>
      </p>
    </el-card>

    <el-card shadow="never" class="block-card">
      <div slot="header">处理全部资源（按设置压缩）</div>
      <p class="desc">
        遍历当前图片/视频资源，按系统设置最大宽高压缩，压缩后覆盖原文件且文件名不变。处理任务在后台异步执行，执行中按钮不可点击。
      </p>
      <el-button
        type="warning"
        :loading="compressRunning"
        :disabled="compressRunning"
        @click="handleStartCompress"
      >
        {{ compressRunning ? "处理中" : "处理全部资源" }}
      </el-button>
      <p v-if="compressLastResult" class="cleanup-result">
        最近一次处理：扫描 {{ compressLastResult.scanned_files || 0 }} 个文件，压缩
        {{ compressLastResult.compressed_files || 0 }} 个文件，失败
        {{ compressLastResult.failed_files || 0 }} 个文件
        <span v-if="compressLastResult.error" class="color-danger">（失败：{{ compressLastResult.error }}）</span>
      </p>
    </el-card>
  </div>
</template>

<script>
import moment from "moment";
import {
  downloadDataBackup,
  uploadDataRestore,
  getNovels,
  deleteNovel,
  updateNovel,
  getResourceCleanupStatus,
  startResourceCleanup,
  getResourceCompressStatus,
  startResourceCompress,
  listBackups,
  downloadBackup,
  restoreBackup,
  deleteBackup,
  getDbLastUpdated
} from "@/net/api";

export default {
  name: "SystemAdmin",
  data() {
    return {
      downloadLoading: false,
      restoreLoading: false,
      fileList: [],
      selectedFile: null,
      novels: [],
      cleanupRunning: false,
      cleanupLastResult: null,
      compressRunning: false,
      compressLastResult: null,
      taskPollTimer: null,
      backups: [],
      backupListLoading: false,
      dbLastUpdated: null,
      backupKeepCount: 48
    };
  },
  computed: {
    currentNovelKey() {
      return localStorage.getItem("current_novel_key") || "default";
    }
  },
  created() {
    this.loadNovels();
    this.fetchSystemTaskStatus();
    this.loadBackups();
    this.fetchDbLastUpdated();
  },
  beforeDestroy() {
    this.stopTaskPolling();
  },
  methods: {
    async fetchSystemTaskStatus() {
      try {
        const [cleanupRes, compressRes] = await Promise.all([
          getResourceCleanupStatus(),
          getResourceCompressStatus()
        ]);
        this.cleanupRunning = !!cleanupRes?.data?.running;
        this.cleanupLastResult = cleanupRes?.data?.last_result || null;
        this.compressRunning = !!compressRes?.data?.running;
        this.compressLastResult = compressRes?.data?.last_result || null;
        if (this.cleanupRunning || this.compressRunning) {
          this.startTaskPolling();
        } else {
          this.stopTaskPolling();
        }
      } catch (e) {
        this.stopTaskPolling();
      }
    },
    async fetchCleanupStatus() {
      await this.fetchSystemTaskStatus();
    },
    startTaskPolling() {
      if (this.taskPollTimer) return;
      this.taskPollTimer = setInterval(() => {
        this.fetchSystemTaskStatus();
      }, 3000);
    },
    stopTaskPolling() {
      if (!this.taskPollTimer) return;
      clearInterval(this.taskPollTimer);
      this.taskPollTimer = null;
    },
    async handleStartCleanup() {
      if (this.cleanupRunning) return;
      try {
        await this.$confirm(
          "将清理所有未被业务数据引用的图片/视频资源。清理后无法恢复，确认继续？",
          "二次确认",
          { type: "warning", confirmButtonText: "确认清理", cancelButtonText: "取消" }
        );
      } catch (e) {
        if (e !== "cancel") throw e;
        return;
      }

      try {
        const { data } = await startResourceCleanup();
        this.$message.success(data?.message || "清理任务已启动");
        this.cleanupRunning = true;
        this.startTaskPolling();
      } catch (error) {
        const msg = error.response?.data?.message || "启动清理失败";
        this.$message.error(msg);
        await this.fetchSystemTaskStatus();
      }
    },
    async handleStartCompress() {
      if (this.compressRunning) return;
      try {
        await this.$confirm(
          "将遍历所有资源并按系统设置进行压缩，压缩后覆盖原文件且文件名不变。确认继续？",
          "二次确认",
          { type: "warning", confirmButtonText: "确认处理", cancelButtonText: "取消" }
        );
      } catch (e) {
        if (e !== "cancel") throw e;
        return;
      }

      try {
        const { data } = await startResourceCompress();
        this.$message.success(data?.message || "资源处理任务已启动");
        this.compressRunning = true;
        this.startTaskPolling();
      } catch (error) {
        const msg = error.response?.data?.message || "启动资源处理失败";
        this.$message.error(msg);
        await this.fetchSystemTaskStatus();
      }
    },
    handleFileChange(file, fileList) {
      this.fileList = fileList.slice(-1);
      this.selectedFile = file && file.raw ? file.raw : null;
    },
    handleFileRemove() {
      this.fileList = [];
      this.selectedFile = null;
    },
    async handleDownloadBackup() {
      if (this.downloadLoading) return;
      this.downloadLoading = true;
      try {
        const { data } = await downloadDataBackup();
        const rel = (data && data.url) || "";
        if (!rel) {
          this.$message.error("服务器未返回下载地址");
          return;
        }
        const href = rel.startsWith("http") ? rel : `${window.location.origin}${rel}`;
        window.open(href, "_blank");
        this.$message.success((data && data.message) || "已开始下载");
      } catch (error) {
        const msg =
          (error.response && error.response.data && error.response.data.message) ||
          (typeof error.response?.data === "string" ? error.response.data : null);
        this.$message.error(msg || "打包备份失败");
      } finally {
        this.downloadLoading = false;
      }
    },
    async handleRestore() {
      if (!this.selectedFile) {
        this.$message.warning("请先选择 .db 文件");
        return;
      }
      try {
        await this.$confirm(
          "将使用上传的数据库文件替换服务器当前数据库；系统会自动保留一份旧数据库备份。确定继续？",
          "危险操作",
          { type: "warning", confirmButtonText: "确定上传", cancelButtonText: "取消" }
        );
      } catch (e) {
        if (e !== "cancel") throw e;
        return;
      }

      this.restoreLoading = true;
      try {
        const { data } = await uploadDataRestore(this.selectedFile);
        this.$message.success(data.message || "恢复成功");
        this.fileList = [];
        this.selectedFile = null;
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          (error.response?.status === 413 ? "文件过大" : null) ||
          "上传或恢复失败";
        this.$message.error(msg);
      } finally {
        this.restoreLoading = false;
      }
    },
    async loadBackups() {
      this.backupListLoading = true;
      try {
        const [backupRes, timeRes] = await Promise.all([listBackups(), getDbLastUpdated()]);
        this.backups = backupRes.data || [];
        this.dbLastUpdated = timeRes.data?.last_updated || null;
      } catch (e) {
        this.$message.error("加载备份列表失败");
      } finally {
        this.backupListLoading = false;
      }
    },
    async fetchDbLastUpdated() {
      try {
        const { data } = await getDbLastUpdated();
        this.dbLastUpdated = data?.last_updated || null;
      } catch (e) {
        // 忽略，不影响主要功能
      }
    },
    async handleBackupDownload(filename) {
      try {
        const response = await downloadBackup(filename);
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.$message.success("开始下载备份文件");
      } catch (error) {
        const msg = error.response?.data?.message || "下载备份失败";
        this.$message.error(msg);
      }
    },
    async handleBackupRestore(row) {
      try {
        await this.$confirm(
          `将使用 ${this.formatTimestamp(row.db_mtime || row.mtime)} 的备份 "${row.filename}" 替换当前数据库。系统会自动保留一份旧数据库备份。确定继续？`,
          "恢复数据库",
          { type: "warning", confirmButtonText: "确定恢复", cancelButtonText: "取消" }
        );
      } catch (e) {
        if (e !== "cancel") throw e;
        return;
      }
      try {
        const { data } = await restoreBackup(row.filename);
        this.$message.success(data?.message || "恢复成功");
        this.loadBackups();
        this.fetchDbLastUpdated();
      } catch (error) {
        const msg = error.response?.data?.message || "恢复失败";
        this.$message.error(msg);
      }
    },
    async handleBackupDelete(row) {
      try {
        await this.$confirm(
          `确定要删除备份 "${row.filename}" 吗？此操作不可恢复。`,
          "删除备份",
          { type: "warning", confirmButtonText: "确定删除", cancelButtonText: "取消" }
        );
      } catch (e) {
        if (e !== "cancel") throw e;
        return;
      }
      try {
        await deleteBackup(row.filename);
        this.$message.success("已删除");
        this.loadBackups();
      } catch (error) {
        const msg = error.response?.data?.message || "删除失败";
        this.$message.error(msg);
      }
    },
    formatSize(bytes) {
      if (!bytes && bytes !== 0) return "";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
    formatTimestamp(ts) {
      if (!ts) return "";
      return moment(ts * 1000).format("YYYY-MM-DD HH:mm:ss");
    },
    async loadNovels() {
      try {
        const { data } = await getNovels();
        this.novels = data || [];
      } catch (e) {
        this.$message.error("加载故事列表失败");
      }
    },
    async handleEditNovel(novel) {
      try {
        const { value } = await this.$prompt("请输入新的故事名称", "编辑名称", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          inputValue: novel.display_name,
          inputPattern: /\S+/,
          inputErrorMessage: "名称不能为空"
        });
        if (value && value.trim() !== novel.display_name) {
          await updateNovel(novel.novel_key, { display_name: value.trim() });
          this.$message.success("修改成功");
          await this.loadNovels();
          // Update local storage if it's the current novel
          const currentNovelKey = localStorage.getItem("current_novel_key");
          if (currentNovelKey === novel.novel_key) {
            localStorage.setItem("current_novel_name", value.trim());
            // Optionally trigger a global event or refresh to update UI elsewhere
            window.location.reload(); // Simplest way to ensure all components see the change
          }
        }
      } catch (e) {
        if (e !== "cancel") {
          this.$message.error(e.response?.data?.message || "修改失败");
        }
      }
    },
    async handleDeleteNovel(novel) {
      try {
        await this.$confirm(
          `确定要彻底删除故事 "${novel.display_name}" 吗？此操作将删除该故事的所有资产、分集、分段和生成记录，且不可恢复！`,
          "极端危险操作",
          {
            confirmButtonText: "确定删除",
            cancelButtonText: "取消",
            type: "warning"
          }
        );
        await deleteNovel(novel.novel_key);
        this.$message.success("删除成功");
        const currentNovelKey = localStorage.getItem("current_novel_key");
        if (currentNovelKey === novel.novel_key) {
          localStorage.removeItem("current_novel_key");
          localStorage.removeItem("current_novel_name");
        }
        await this.loadNovels();
      } catch (e) {
        if (e !== "cancel") {
          this.$message.error(e.response?.data?.message || "删除失败");
        }
      }
    }
  }
};
</script>

<style scoped>
.system-admin {
  max-width: 720px;
}
.hint {
  color: #909399;
  margin-bottom: 20px;
  line-height: 1.6;
}
.block-card {
  margin-bottom: 20px;
}
.desc {
  color: #606266;
  margin-bottom: 16px;
  line-height: 1.6;
}
.desc code {
  background: #f4f4f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}
.color-danger {
  color: #f56c6c;
}
.cleanup-result {
  margin-top: 12px;
  color: #606266;
}
</style>
